import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import { useState } from 'react';
import { getTimeLeftString } from '../../../utils/methods';
import AvatarGroupStacked from '../components/AvatarGroupStacked';

const AchievementDetailModal = ({ open, onClose, achievement, getAchievementColors, isLocked = false }) => {
    const [expanded, setExpanded] = useState(false);
    if (!achievement) return null;
    const difficultyColors = getAchievementColors ? getAchievementColors(achievement.achievement_type) : { bgcolor: '#e0e0e0', color: '#333' };
    const unlockDate = achievement.date ? new Date(achievement.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '';
    const completedAt = achievement.completed_at ? new Date(achievement.completed_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '';

    // Choose description based on lock status
    const displayDescription = isLocked && achievement.locked_description 
        ? achievement.locked_description 
        : achievement.description;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    {isLocked ? '🔒 Achievement' : '🏆 Achievement'}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 0 }}>
                {/* Imagen grande con overlay expand */}
                <Box
                    sx={{
                        position: 'relative',
                        height: expanded ? '57vh' : '200px',
                        backgroundImage: `url(${achievement.signed_image_url || ''})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        transition: 'height 0.3s cubic-bezier(.4,2,.6,1)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        filter: isLocked ? 'grayscale(100%)' : 'none',
                        '&::before': isLocked ? {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: `${difficultyColors.bgcolor}60`,
                            zIndex: 1,
                        } : {},
                    }}
                    onClick={() => setExpanded(!expanded)}
                >
                    {isLocked && (
                        <Box
                            component="img"
                            src={achievement.signed_thumbnail_url || ''}
                            sx={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '50%',
                                border: `3px solid ${difficultyColors.bgcolor}`,
                                position: 'relative',
                                zIndex: 2,
                                filter: 'grayscale(100%)',
                            }}
                        />
                    )}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            zIndex: 2,
                            opacity: 0.85,
                            transition: 'opacity 0.2s',
                            '&:hover': { opacity: 1 },
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: '50%',
                        }}
                    >
                        <IconButton size="small">
                            {expanded ? <IconComponent iconName="contract" /> : <IconComponent iconName="expand" />}
                        </IconButton>
                    </Box>
                </Box>
                {/* Info */}
                <Box sx={{ p: 3, pt: 2 }}>
                    <Chip
                        size="small"
                        label={achievement.achievement_type}
                        sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            bgcolor: difficultyColors.bgcolor,
                            color: difficultyColors.color,
                            mb: 1.5,
                            textTransform: 'capitalize',
                            '& .MuiChip-label': {
                                color: difficultyColors.color,
                            },
                        }}
                    />
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 1, mt: 0 }}>{achievement.name}</Typography>

                    {/* Show date for unlocked achievements */}
                    {!isLocked && (completedAt || unlockDate) && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                            Unlocked on {completedAt || unlockDate}
                        </Typography>
                    )}

                    {/* Show time left for locked achievements if available */}
                    {isLocked && achievement.due_time && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1, color: difficultyColors.bgcolor }}>
                            {getTimeLeftString(achievement.due_time)}
                        </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 13, lineHeight: 1.5, opacity: 0.85, mb: 2 }}>
                        {displayDescription}
                    </Typography>

                    {achievement?.comment && !isLocked &&
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 13, lineHeight: 1.5, opacity: 0.85, mb: 2 }}>
                            Comment: {achievement.comment}
                        </Typography>
                    }

                    {/* Hide other users for locked achievements */}
                    {!isLocked && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', gap: 0, flexDirection: 'column' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 300, fontSize: 12 }}>
                                {achievement.usernames && achievement.usernames.length > 0 
                                    ? 'Other users with this achievement' 
                                    : 'No other users with this achievement'}
                            </Typography>
                            <AvatarGroupStacked usernames={achievement.usernames || []} users_photo_links={achievement.photo_links || []} maxAvatars={3} useRandomColors={true} />
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AchievementDetailModal; 
 